import { Button, Drawer, Flex, Text } from '@mantine/core';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { ToggleRequirementItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/components/ToggleRequirementItem.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { InvestmentPlaceholder } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentPlaceholder/InvestmentPlaceholder.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useManageLegalDocumentsDrawer } from './useManageLegalDocumentsDrawer.ts';

interface Props {
	opened: boolean;
	onClose: () => void;
}

export const ManageLegalDocumentsDrawer = ({ opened, onClose }: Props) => {
	const tMipManage = useTranslationMipManage();
	const {
		masterDocuments,
		canMutateLegalDocuments,
		isLoading,
		isError,
		pendingIds,
		onToggle,
		goToMasterDocuments,
	} = useManageLegalDocumentsDrawer(opened);

	const renderBody = () => {
		if (isLoading) {
			return <InlineLoader />;
		}

		if (isError) {
			return (
				<InvestmentPlaceholder
					icon="warning"
					label={tMipManage('manage_legal_documents_drawer.load_error')}
					mih={168}
				/>
			);
		}

		if (masterDocuments.length === 0) {
			return (
				<InvestmentPlaceholder
					icon="folder"
					label={tMipManage('manage_legal_documents_drawer.empty_label')}
					mih={168}
				/>
			);
		}

		return (
			<Flex direction="column" gap="1rem">
				{masterDocuments.map((doc, index) => (
					<ToggleRequirementItem
						key={doc.templateId}
						title={doc.name}
						description={doc.instruction ?? ''}
						checked={doc.isEnabled}
						disabled={!canMutateLegalDocuments || pendingIds.includes(doc.templateId)}
						onToggle={() => onToggle(doc.templateId, doc.isEnabled)}
						showBorder={index < masterDocuments.length - 1}
					/>
				))}
			</Flex>
		);
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			size="484px"
			transitionProps={drawerTransitionProps}
			title={tMipManage('manage_legal_documents_drawer.title')}
			trapFocus={false}
			styles={{
				header: {
					paddingBottom: '0.5rem',
				},
			}}
		>
			<Flex direction="column" h="100%">
				<Text c="dark.0" mb="2rem">
					{tMipManage('manage_legal_documents_drawer.subtitle')}
				</Text>

				{renderBody()}

				<Button
					variant="default"
					size="md"
					leftSection={<Icon variant="plus" size="xl" />}
					mt="1.5rem"
					fw={600}
					onClick={goToMasterDocuments}
					styles={{
						root: {
							borderStyle: 'dashed',
						},
					}}
				>
					{tMipManage('manage_legal_documents_drawer.button')}
				</Button>
			</Flex>
		</Drawer>
	);
};
